import { useCallback, useEffect, useMemo, useState } from 'react';
import { PageShell } from '../layout/page-shell.jsx';
import { api, ApiError } from '../../api/index.js';
import { useToast } from '../ui/toast.jsx';
import { formatUtcInTimezone } from '../../lib/time.js';

/**
 * PoC Recordings browser — operator view of audio files captured by
 * LiveKit Egress on the local node.
 *
 * Phase 2.5 step 2 / Phase 3.7. Polls /api/poc/recordings every 10 s.
 * Per recording shows: channel name + start time + duration + size +
 * participants + actions (play inline, download, delete — admin only).
 *
 * Egress availability: when the node has POC_EGRESS_ENABLED=false a
 * yellow banner tells the operator «recording disabled on the node»;
 * the list is shown unchanged (older recordings can still be browsed).
 */

const POLL_MS = 10_000;

/** Wall-clock timestamp with viewer-TZ suffix (e.g. «21 Jun 2026,
 *  10:33:00 EEST»). UTC storage convention; UI re-renders per the
 *  signed-in user's timezone pref. */
function fmtTs(ms, tz) {
  if (!ms) return '—';
  try {
    return formatUtcInTimezone(new Date(ms).toISOString(), tz || 'UTC');
  } catch {
    return '—';
  }
}

function fmtDuration(ms) {
  if (!ms || ms <= 0) return '—';
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${m}m ${sec}s`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
}

function fmtBytes(b) {
  if (!b) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0; let n = b;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(n >= 100 ? 0 : 1)} ${u[i]}`;
}

function MetricCard({ label, value, tone = 'ink', icon }) {
  const cls =
        tone === 'lime'  ? 'text-lime-700'
      : tone === 'sky'   ? 'text-sky-700'
      : tone === 'amber' ? 'text-amber-700'
      : tone === 'rose'  ? 'text-rose-700'
      :                    'text-ink';
  return (
    <div className="rounded-2xl border border-line bg-white px-4 py-3 shadow-card">
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-mist">
        {icon ? <i className={`fa-solid ${icon}`} aria-hidden="true" /> : null}
        <span>{label}</span>
      </div>
      <div className={`mt-1 text-2xl font-semibold tabular-nums ${cls}`}>{value}</div>
    </div>
  );
}

function Player({ src, onClose }) {
  return (
    <div className="mt-2 flex items-center gap-2">
      <audio controls autoPlay src={src} className="h-9 flex-1" />
      <button
        type="button"
        onClick={onClose}
        className="rounded-md border border-line bg-white px-2 py-1 text-xs text-mist hover:bg-slate-50"
      >
        <i className="fa-solid fa-xmark mr-1" aria-hidden="true" />
        Close
      </button>
    </div>
  );
}

function ParticipantsCell({ participants }) {
  if (!Array.isArray(participants) || participants.length === 0) {
    return <span className="text-mist">—</span>;
  }
  const names = participants
    .map(p => p.callsign || (p.identity ? p.identity.slice(0, 8) : ''))
    .filter(Boolean);
  return (
    <div className="flex flex-wrap gap-1">
      {names.map((n, i) => (
        <span key={i} className="inline-flex items-center rounded-md bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-700 ring-1 ring-slate-200">
          {n}
        </span>
      ))}
    </div>
  );
}

export function PocRecordingsPage({ currentUser }) {
  const { success, error: showError } = useToast();
  const [data, setData]         = useState({ recordings: [], summary: { count: 0, bytes: 0 }, active: [], egress: { enabled: false } });
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [lastTs, setLastTs]     = useState(null);
  const [playingId, setPlaying] = useState(null);
  const [confirmId, setConfirm] = useState(null);

  const isAdmin = currentUser?.role === 'admin';
  const tz = currentUser?.timezone || 'UTC';

  const fetchData = useCallback(async () => {
    try {
      const resp = await api.poc.listRecordings();
      setData({
        recordings: Array.isArray(resp?.recordings) ? resp.recordings : [],
        summary:    resp?.summary || { count: 0, bytes: 0 },
        active:     Array.isArray(resp?.active) ? resp.active : [],
        egress:     resp?.egress || { enabled: false },
      });
      setLastTs(Number(resp?.ts) || Date.now());
      setError(resp?.error || null);
    } catch (e) {
      if (e instanceof ApiError) setError(e.message);
      else                       setError(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const t = setInterval(fetchData, POLL_MS);
    return () => clearInterval(t);
  }, [fetchData]);

  const handleDelete = useCallback(async (rec) => {
    try {
      await api.poc.deleteRecording(rec.channelUuid, rec.id);
      success(`Recording deleted`);
      setConfirm(null);
      fetchData();
    } catch (e) {
      showError(`Delete failed: ${e?.message || e}`);
    }
  }, [fetchData, success, showError]);

  const lastTsLabel = lastTs ? fmtTs(lastTs, tz) : '—';
  const totalSize   = data.summary?.bytes || 0;
  const total       = data.summary?.count || 0;
  const activeCount = data.active?.length || 0;
  const egressOn    = Boolean(data.egress?.enabled);

  return (
    <PageShell
      icon="fa-circle-dot"
      title="PoC Recordings"
      subtitle="Audio captured by LiveKit Egress on this node. Refreshes every 10 seconds."
      actions={
        <div className="flex items-center gap-3">
          <span className="text-xs text-mist tabular-nums">
            Last refresh: <span className="font-medium text-ink">{lastTsLabel}</span>
          </span>
          <button
            type="button"
            onClick={fetchData}
            className="inline-flex items-center gap-2 rounded-xl border border-line bg-white px-3 py-1.5 text-xs font-medium text-ink shadow-sm hover:bg-slate-50"
          >
            <i className="fa-solid fa-arrows-rotate" aria-hidden="true" />
            Refresh
          </button>
        </div>
      }
    >
      {error ? (
        <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <i className="fa-sharp fa-solid fa-triangle-exclamation mr-2" aria-hidden="true" />
          {error}
        </div>
      ) : null}

      {!egressOn ? (
        <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <i className="fa-sharp fa-solid fa-circle-info mr-2" aria-hidden="true" />
          LiveKit Egress is <strong>disabled</strong> on this node. Existing recordings are listed; new sessions will not be recorded until <code className="px-1 font-mono">POC_EGRESS_ENABLED=true</code> in <code className="px-1 font-mono">/opt/adms/ad-tms-service/.env</code> and the service restarts.
        </div>
      ) : null}

      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <MetricCard label="Total recordings"  value={total}              icon="fa-list-check"   tone="ink"   />
        <MetricCard label="Disk used"         value={fmtBytes(totalSize)} icon="fa-database"    tone="sky"   />
        <MetricCard label="Active now"        value={activeCount}        icon="fa-circle-dot"   tone={activeCount > 0 ? 'rose' : 'ink'} />
        <MetricCard label="Egress"            value={egressOn ? 'ON' : 'OFF'} icon="fa-microphone-lines" tone={egressOn ? 'lime' : 'amber'} />
      </div>

      {loading ? (
        <div className="rounded-2xl border border-dashed border-line bg-white px-4 py-12 text-center text-mist shadow-card">
          <i className="fa-solid fa-spinner fa-spin mr-2" aria-hidden="true" />
          Loading…
        </div>
      ) : data.recordings.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-line bg-white px-4 py-12 text-center shadow-card">
          <i className="fa-solid fa-circle-dot mb-3 text-3xl text-mist" aria-hidden="true"></i>
          <div className="text-base font-medium text-ink">No recordings</div>
          <div className="mt-1 text-sm text-mist">
            Recordings will appear here when a PoC channel with <code className="px-1 font-mono">recording = on</code> is active.
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-line bg-white shadow-card">
          <table className="w-full text-sm">
            <thead className="border-b border-line bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-mist">
              <tr>
                <th className="px-3 py-2.5">Channel</th>
                <th className="px-3 py-2.5">Started</th>
                <th className="px-3 py-2.5">Duration</th>
                <th className="px-3 py-2.5">Size</th>
                <th className="px-3 py-2.5">Participants</th>
                <th className="px-3 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.recordings.map((r) => {
                const isPlaying = playingId === `${r.channelUuid}/${r.id}`;
                const isActive  = !r.endedAtMs; // still recording
                return (
                  <tr key={`${r.channelUuid}/${r.id}`} className="border-b border-line last:border-b-0 hover:bg-slate-50 align-top">
                    <td className="px-3 py-2">
                      <div className="font-medium text-ink">
                        {r.channelName || r.channelUuid.slice(0, 8)}
                        {isActive ? (
                          <span className="ml-2 inline-flex items-center gap-1 rounded-md bg-rose-50 px-1.5 py-0.5 text-[10px] font-bold tracking-wide text-rose-700 ring-1 ring-rose-200">
                            <i className="fa-solid fa-circle text-[7px] animate-pulse" aria-hidden="true"></i>
                            REC
                          </span>
                        ) : null}
                      </div>
                      <div className="mt-0.5 font-mono text-[11px] text-mist">{r.id}</div>
                    </td>
                    <td className="px-3 py-2 text-sm tabular-nums text-ink">{fmtTs(r.startedAtMs, tz)}</td>
                    <td className="px-3 py-2 text-sm tabular-nums">
                      <span className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-700 ring-1 ring-slate-200">
                        <i className="fa-solid fa-clock text-[10px]" aria-hidden="true" />
                        {fmtDuration(r.durationMs)}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-sm tabular-nums text-mist">{fmtBytes(r.sizeBytes)}</td>
                    <td className="px-3 py-2"><ParticipantsCell participants={r.participants} /></td>
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex items-center gap-1">
                        {r.hasAudio ? (
                          <>
                            <button
                              type="button"
                              onClick={() => setPlaying(isPlaying ? null : `${r.channelUuid}/${r.id}`)}
                              className="inline-flex items-center gap-1 rounded-md border border-line bg-white px-2 py-1 text-xs text-ink shadow-sm hover:bg-slate-50"
                              title={isPlaying ? 'Stop playback' : 'Play'}
                            >
                              <i className={`fa-solid ${isPlaying ? 'fa-stop' : 'fa-play'}`} aria-hidden="true"></i>
                              {isPlaying ? 'Stop' : 'Play'}
                            </button>
                            <a
                              href={api.poc.recordingUrl(r.channelUuid, r.id, true)}
                              className="inline-flex items-center gap-1 rounded-md border border-line bg-white px-2 py-1 text-xs text-ink shadow-sm hover:bg-slate-50"
                              title="Download"
                            >
                              <i className="fa-solid fa-download" aria-hidden="true"></i>
                            </a>
                          </>
                        ) : (
                          <span className="text-xs text-mist">no audio</span>
                        )}
                        {isAdmin && !isActive ? (
                          <button
                            type="button"
                            onClick={() => setConfirm(`${r.channelUuid}/${r.id}`)}
                            className="inline-flex items-center gap-1 rounded-md border border-rose-200 bg-rose-50 px-2 py-1 text-xs text-rose-700 shadow-sm hover:bg-rose-100"
                            title="Delete recording"
                          >
                            <i className="fa-solid fa-trash" aria-hidden="true"></i>
                          </button>
                        ) : null}
                      </div>
                      {isPlaying ? (
                        <Player
                          src={api.poc.recordingUrl(r.channelUuid, r.id)}
                          onClose={() => setPlaying(null)}
                        />
                      ) : null}
                      {confirmId === `${r.channelUuid}/${r.id}` ? (
                        <div className="mt-2 flex items-center justify-end gap-2">
                          <span className="text-xs text-rose-700">Delete this recording?</span>
                          <button
                            type="button"
                            onClick={() => handleDelete(r)}
                            className="rounded-md border border-rose-300 bg-rose-600 px-2 py-1 text-xs font-medium text-white hover:bg-rose-700"
                          >
                            Confirm
                          </button>
                          <button
                            type="button"
                            onClick={() => setConfirm(null)}
                            className="rounded-md border border-line bg-white px-2 py-1 text-xs text-mist hover:bg-slate-50"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </PageShell>
  );
}
